import Head from 'next/head'
import * as React from 'react'
import { useRouter } from 'next/router'

const languagePriority = {
  en: 0,
  es: 1,
  fr: 2,
  de: 3,
  it: 4,
  pt: 5,
  nl: 6,
  sv: 7,
  ar: 8
}
const marketPriority = {
  XX: 0,
  US: 1,
  CA: 2,
  GB: 3,
  AU: 4,
  ES: 5,
  FR: 6,
  NL: 7,
  PT: 8,
  DE: 9,
  IT: 11,
  AE: 12,
  CH: 13,
  FI: 14,
  CO: 15,
  BE: 16,
  SE: 17,
  MX: 18
}

function getHighestPriorityVariant(priorityArray, variants) {
  let highestPriorityIndex = Infinity
  let highestPriorityVariant = undefined

  for (const variant of variants) {
    // Find the index of the variant's marketIsoCode in the priority list
    const currentIndex = priorityArray.findIndex((iso) => iso === variant.marketIsoCode)

    // Only proceed if the marketIsoCode was found
    if (currentIndex !== -1) {
      // Check if this variant has a higher priority (a lower index)
      if (currentIndex < highestPriorityIndex) {
        highestPriorityIndex = currentIndex
        highestPriorityVariant = variant
      }
    }
  }

  return highestPriorityVariant
}

/**
 * @param {Object} path
 * @returns {string}
 */
export const getDefaultXDefault = (path, router) => {
  if (router.pathname === '/integrations/[slug]' && router.query.slug) {
    return `${process.env.NEXT_PUBLIC_SITE_URL}/en/integrations/${router.query.slug}`
  }

  if (!path) return `${process.env.NEXT_PUBLIC_SITE_URL}/en`
  if (router.pathname === '/') return `${process.env.NEXT_PUBLIC_SITE_URL}/en`

  const availableLanguages = new Set(
    Object.values(path)
      .map((item) => item.locale)
      .flat()
  )
  const availableMarkets = Object.values(path)
    .map((item) => item.marketIsoCode)
    .flat()

  // get the language with the highest priority
  const language = Array.from(availableLanguages).sort(
    (a, b) => languagePriority[a] - languagePriority[b]
  )[0]

  const pathsWithLanguage = Object.values(path).filter((item) => item.locale === language)

  const sortedMarkets = availableMarkets.sort((a, b) => marketPriority[a] - marketPriority[b])

  const highestPriorityPath = getHighestPriorityVariant(sortedMarkets, pathsWithLanguage)

  if (!highestPriorityPath) {
    return `${process.env.NEXT_PUBLIC_SITE_URL}/en`
  }

  if (router.pathname === '/blog/[category]/[...slug]') {
    return `${process.env.NEXT_PUBLIC_SITE_URL}/${highestPriorityPath.slugPrefix}/blog/${highestPriorityPath.category}/${highestPriorityPath.slug}`
  }

  if (router.pathname === '/customers/[slug]') {
    return `${process.env.NEXT_PUBLIC_SITE_URL}/${highestPriorityPath.slugPrefix}/customers/${highestPriorityPath.slug}`
  }

  return `${process.env.NEXT_PUBLIC_SITE_URL}/${highestPriorityPath.slugPrefix}/${highestPriorityPath.slug}`
}

const Metatags = ({
  siteName,
  title,
  description,
  canonical,
  alternate = [],
  nofollow,
  nosnippet,
  noarchive,
  maxPreviewImage,
  noindex,
  image,
  pageType,
  preload,
  keywords = false,
  pageLocale = 'en',
  schemaOrg,
  paths = {}
}) => {
  const router = useRouter()
  const metaLinks = []
  const metaTags = []

  if (canonical) {
    metaLinks.push({
      rel: 'canonical',
      href: canonical
    })
  }

  if (Array.isArray(preload)) {
    preload.forEach((preloadLik) => {
      metaLinks.push(preloadLik)
    })
  }

  if (Array.isArray(alternate)) {
    // add default lang
    alternate.push({
      rel: 'alternate',
      hrefLang: 'x-default',
      href: getDefaultXDefault(paths, router)
    })

    alternate.forEach((a) => {
      metaLinks.push(a)
    })
  }

  if (image) {
    metaTags.push({
      name: 'og:image',
      content: image
    })

    metaTags.push({
      name: 'twitter:image',
      content: image
    })
  }

  // Handle robots: nofollow, noindex, noarchive and nosnippet
  const robots = []

  if (nofollow) {
    robots.push('NOFOLLOW')
  }

  if (noindex) {
    robots.push('NOINDEX')
  }

  if (nosnippet) {
    robots.push('NOSNIPPET')
  }

  if (noarchive) {
    robots.push('NOARCHIVE')
  }

  if (maxPreviewImage) {
    robots.push('max-image-preview:large')
  }

  if (robots.length) {
    metaTags.push({
      name: 'robots',
      content: robots.join(', ')
    })
  }

  metaTags.push({
    property: 'og:title',
    content: title
  })

  metaTags.push({
    property: 'twitter:title',
    content: title
  })

  metaTags.push({
    name: 'twitter:card',
    content: 'summary'
  })

  metaTags.push({
    property: 'og:type',
    content: 'website'
  })

  metaTags.push({
    name: 'twitter:card',
    content: 'summary_large_image'
  })

  metaTags.push({
    name: 'og:site_name',
    content: siteName
  })

  if (description) {
    metaTags.push({
      name: 'description',
      content: description
    })
    metaTags.push({
      property: 'og:description',
      content: description
    })
    metaTags.push({
      name: 'twitter:description',
      content: description
    })
  }

  if (keywords) {
    metaTags.push({
      name: 'keywords',
      content: keywords
    })
  }

  const scripts = []

  if (schemaOrg) {
    scripts.push({
      type: 'application/ld+json',
      content: {
        schemaOrg
      }
    })
  }

  return (
    <Head>
      <title>{title}</title>
      {metaTags.map((tag) => (
        <meta key={tag.name || tag.property} {...tag} />
      ))}
      {metaLinks.map((tag, i) => (
        <link key={`meta-link-${i}`} {...tag} />
      ))}
      {scripts.map((script, i) => (
        <script key={i} type={script.type}>
          {JSON.stringify(script.content.schemaOrg, null, 2)}
        </script>
      ))}
      <meta name="theme-color" content="#00d369" />
      <meta name="page-title" data-type="string" content={title} />
      <meta name="page-lang" data-type="string" content={pageLocale} />
      <meta name="page-type" data-type="string" content={pageType} />
      <meta
        name="ahrefs-site-verification"
        content="520d964aacaaf63dc8110284827b688b9f874ee5ff4c45c3de84b5c86b788316"
      />
    </Head>
  )
}

export default Metatags
